import {
  Component,
  Inject,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  DestroyRef,
  ElementRef,
  QueryList,
  ViewChildren,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApiService } from '../../../../core/services/api/api.service';
import { ToastService } from '../../../../core/services/toast/toast.service';
import { IconCacheService } from '../../../../core/services/icon-cache/icon-cache.service';
import { EnfafIconComponent } from '../../../../components/enfaf-icon/enfaf-icon.component';
import { AdminClassReviewsResponse, AdminClassReviewItem } from '../../../../models/interfaces/admin-class-reviews.interface';

export interface AdminReviewsModalData {
  courseClassId: string;
}

@Component({
  selector: 'app-admin-class-reviews-modal',
  templateUrl: './admin-class-reviews-modal.component.html',
  styleUrls: ['./admin-class-reviews-modal.component.scss'],
  imports: [CommonModule, MatDialogModule, MatProgressSpinnerModule, MatButtonModule, EnfafIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true
})
export class AdminClassReviewsModalComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly api = inject(ApiService);
  private readonly toast = inject(ToastService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dialogRef = inject(MatDialogRef<AdminClassReviewsModalComponent>);
  private readonly iconRegistry = inject(MatIconRegistry);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly iconCache = inject(IconCacheService);

  isLoading = false;
  reviewsData: AdminClassReviewsResponse | null = null;
  currentPage = 0;
  allReviews: AdminClassReviewItem[] = [];
  currentPageReviews: AdminClassReviewItem[] = [];
  totalPages = 0;
  readonly ITEMS_PER_PAGE = 4;
  readonly Math = Math;
  expandedReviews = new Set<string>();
  truncatedReviews = new Set<string>();

  @ViewChildren('commentEl') commentElements!: QueryList<ElementRef<HTMLSpanElement>>;

  constructor(@Inject(MAT_DIALOG_DATA) public data: AdminReviewsModalData) {
    // Register star icons from assets/icons/ (different path than IconCacheService default)
    const starIcons = ['star-gray-outline', 'star-gray-filled', 'star-blue-outline', 'star-blue-filled'];
    starIcons.forEach((name) => this.iconRegistry.addSvgIcon(name, this.sanitizer.bypassSecurityTrustResourceUrl(`assets/icons/${name}.svg`)));
    // Mark as registered so EnfafIconComponent won't re-register from wrong path
    this.iconCache.markAsRegistered(...starIcons);
  }

  ngOnInit(): void {
    this.loadAllReviews();
  }

  private scheduleDetectTruncation(): void {
    // Wait one microtask so Angular finishes rendering the DOM
    setTimeout(() => this.detectTruncation(), 0);
  }

  private detectTruncation(): void {
    this.commentElements.forEach((elRef) => {
      const el = elRef.nativeElement;
      const reviewId = el.getAttribute('data-review-id');
      if (!reviewId) return;
      if (el.scrollWidth > el.clientWidth) {
        this.truncatedReviews.add(reviewId);
      } else {
        this.truncatedReviews.delete(reviewId);
      }
    });
    this.cdr.markForCheck();
  }

  isTruncated(reviewId: string): boolean {
    return this.truncatedReviews.has(reviewId);
  }

  loadAllReviews(): void {
    if (this.isLoading) return;

    this.isLoading = true;
    this.cdr.markForCheck();

    console.log('[AdminReviewsModal] Loading reviews for classId:', this.data.courseClassId);

    // Load all reviews at once (max pageSize is 100 per backend validation)
    this.api
      .getAdminClassReviews(this.data.courseClassId, 0, 100)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: AdminClassReviewsResponse) => {
          console.log('[AdminReviewsModal] Reviews loaded successfully:', response);
          this.reviewsData = response;
          this.allReviews = response.reviews;
          this.calculatePages();
          this.updateCurrentPageReviews();
          this.isLoading = false;
          this.cdr.markForCheck();
          this.scheduleDetectTruncation();
        },
        error: (error) => {
          console.error('[AdminReviewsModal] Error loading reviews:', error);
          console.error('[AdminReviewsModal] Error status:', error.status);
          console.error('[AdminReviewsModal] Error message:', error.message);
          console.error('[AdminReviewsModal] Error response:', error.error);
          this.toast.presentErrorToast('Error al cargar las valoraciones');
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  calculatePages(): void {
    this.totalPages = Math.max(1, Math.ceil(this.allReviews.length / this.ITEMS_PER_PAGE));
  }

  updateCurrentPageReviews(): void {
    const start = this.currentPage * this.ITEMS_PER_PAGE;
    const end = start + this.ITEMS_PER_PAGE;
    this.currentPageReviews = this.allReviews.slice(start, end);
  }

  goToPage(page: number): void {
    if (page === this.currentPage || page < 0 || page >= this.totalPages) return;
    this.currentPage = page;
    this.expandedReviews.clear();
    this.updateCurrentPageReviews();
    this.cdr.markForCheck();
    this.scheduleDetectTruncation();
  }

  prevPage(): void {
    if (this.currentPage > 0) {
      this.goToPage(this.currentPage - 1);
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages - 1) {
      this.goToPage(this.currentPage + 1);
    }
  }

  getPaginationNumbers(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i);
  }

  close(): void {
    this.dialogRef.close();
  }

  toggleExpanded(reviewId: string): void {
    if (this.expandedReviews.has(reviewId)) {
      this.expandedReviews.delete(reviewId);
    } else {
      this.expandedReviews.add(reviewId);
    }
    this.cdr.markForCheck();
  }

  isExpanded(reviewId: string): boolean {
    return this.expandedReviews.has(reviewId);
  }

  get shouldDistributeReviewCards(): boolean {
    return this.currentPageReviews.length === this.ITEMS_PER_PAGE && this.expandedReviews.size === 0;
  }

  getStarArray(): number[] {
    return [1, 2, 3, 4, 5];
  }

  getRoundedAverageRating(value?: number | null): number {
    return Math.round((value || 0) * 10) / 10;
  }

  getFullStars(value?: number | null): number {
    return Math.floor(this.getRoundedAverageRating(value));
  }

  getPartialStarPercentage(value?: number | null): number {
    const roundedValue = this.getRoundedAverageRating(value);
    const decimalPart = roundedValue - Math.floor(roundedValue);
    const rawPercent = Math.round(decimalPart * 100);
    const steppedPercent = Math.round(rawPercent / 10) * 10;

    const areaToWidthMap: Record<number, number> = {
      0: 0,
      10: 26,
      20: 33,
      30: 40,
      40: 45,
      50: 50,
      60: 55,
      70: 60,
      80: 67,
      90: 74,
      100: 100
    };

    return areaToWidthMap[steppedPercent] ?? 0;
  }

  getReviewPartialStarPercentage(value: number): number {
    const decimalPart = value - Math.floor(value);
    const rawPercent = Math.round(decimalPart * 100);
    const steppedPercent = Math.round(rawPercent / 10) * 10;

    const areaToWidthMap: Record<number, number> = {
      0: 0,
      10: 26,
      20: 33,
      30: 40,
      40: 45,
      50: 50,
      60: 55,
      70: 60,
      80: 67,
      90: 74,
      100: 100
    };

    return areaToWidthMap[steppedPercent] ?? 0;
  }

  formatAverageRating(value?: number | null): string {
    const roundedValue = this.getRoundedAverageRating(value);

    if (Number.isInteger(roundedValue)) {
      return roundedValue.toString();
    }

    return roundedValue.toFixed(1).replace('.', ',');
  }
}
